import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  SFTypography,
  SFButton,
  SFIcons,
  SFProgress,
  SFStack,
  SFLoadingSpinner,
  Colors,
} from "@sf-core";
import { saveCourseTrack } from "../../services/courseService";
import { enrollInDomain } from "../../redux/reducers/domainReducer";
import { STATUS } from "../../constants/statuses";
import { getCourseUrl } from "../../constants/urls";

export const SkillPathPlayButton = ({
  item,
  courses = [],
  skillPathInfo,
  preview,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [skillpathStatus, setSkillpathStatus] = useState(null);
  const [nextCourse, setNextCourse] = useState(null);
  const {
    skillPathId,
    completedDuration,
    totalDuration,
    completedCourses,
    totalCourses,
  } = item;
  const { domainId, domainStatus } = skillPathInfo;

  let buttonLabel = null;
  let progressLiteral = t("lxpComponents.basicDetails.completedCourses", {
    completed: completedCourses,
    total: totalCourses,
  });

  if (skillpathStatus === STATUS.NOT_STARTED) {
    buttonLabel = t("lxpComponents.basicDetails.button.startLabel");
    progressLiteral = t("lxpComponents.basicDetails.button.notStarted");
  } else if (skillpathStatus === STATUS.IN_PROGRESS) {
    buttonLabel = t("lxpComponents.basicDetails.button.resumeLabel");
  }

  useEffect(() => {
    if (preview) {
      setSkillpathStatus(STATUS.NOT_STARTED);
    } else {
      // Calculate the status of the skillpath based on the courses
      let requiredCourses = courses.filter(
        (course) => course.required === true
      );

      if (requiredCourses.length === 0) {
        requiredCourses = courses;
      }

      const getStatus = (courses) => {
        const allNotStarted = courses.every(
          (course) => course.status === STATUS.NOT_STARTED
        );
        const allCompleted = courses.every(
          (course) => course.status === STATUS.COMPLETED
        );

        if (allNotStarted) {
          return STATUS.NOT_STARTED;
        } else if (allCompleted) {
          return STATUS.COMPLETED;
        } else {
          return STATUS.IN_PROGRESS;
        }
      };
      setSkillpathStatus(getStatus(requiredCourses));

      //Find the next course to navigate to
      let sortedCourses = requiredCourses.sort((a, b) => a.rank - b.rank);
      const nextCourse =
        sortedCourses.find((course) => course.status === STATUS.IN_PROGRESS) ||
        sortedCourses.find((course) => course.status === STATUS.NOT_STARTED);
      setNextCourse(nextCourse);
    }
  }, [courses]);

  const handleClick = async () => {
    if (!preview) {
      setLoading(true);
      if (domainStatus === STATUS.NOT_STARTED) {
        dispatch(enrollInDomain(domainId));
      }
      // Request to track the skillPath id from where the course was accessed
      let data = {
        skillPathId: skillPathId,
        contentId: nextCourse.id,
      };
      saveCourseTrack(data);
      navigate(getCourseUrl(nextCourse.id));
      setLoading(false);
    }
  };

  return (
    <SFStack
      direction="row"
      justifyContent="flex-start"
      alignItems="center"
      spacing={2.5}
    >
      {buttonLabel && (
        <SFButton
          variant="secondary"
          size={"large"}
          onClick={loading ? () => {} : handleClick}
        >
          <SFStack spacing={1} direction="row" alignItems={"center"}>
            {loading ? (
              <SFLoadingSpinner size={"large"} />
            ) : (
              <SFIcons.SFPlayIcon size={"large"} />
            )}
            <SFTypography>{buttonLabel}</SFTypography>
          </SFStack>
        </SFButton>
      )}
      <SFProgress
        title={progressLiteral}
        value={preview ? 0 : (completedDuration / totalDuration) * 100}
        maxWidth="300px"
        narrow
        completeColor={Colors.GREEN300}
        color={Colors.BLUE300}
      />
    </SFStack>
  );
};

export default SkillPathPlayButton;
